import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import VideoJS from "src/components/compounds/VideoJS/VideoJS";
import { useGetTeamByIDQuery, useGetTeamVideoAssetsByIDQuery } from "src/state/modules/api";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { downloadHlsVideo, DownloadCanceledError, hlsDownloadExtension, preflightHlsPlayable } from "src/utils/download-hls";
import * as Team from "src/state/modules/team";
import Styles from "./VideoPage.module.scss";

type DownloadStatus = "idle" | "downloading" | "done" | "error";

// Per-clip download state. Each VideoCard owns its own instance so progress and errors are
// independent, and an AbortController lets the user cancel a long download mid-flight.
function useVideoDownload() {
  const [status, setStatus] = useState<DownloadStatus>("idle");
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(async (playlistUrl: string, filename: string) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus("downloading");
    setPercent(0);
    setError(null);

    try {
      await downloadHlsVideo(
        playlistUrl,
        filename,
        ({ completed, total }) => setPercent(total ? Math.round((completed / total) * 100) : 0),
        controller.signal
      );
      setStatus("done");
    } catch (err) {
      // Aborting the fetch or dismissing the save dialog is a quiet cancel, not an error.
      if (controller.signal.aborted || err instanceof DownloadCanceledError) {
        setStatus("idle");
        return;
      }
      setError(err instanceof Error ? err.message : "Download failed");
      setStatus("error");
    } finally {
      abortRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  return { status, percent, error, start, cancel };
}

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12 3a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V4a1 1 0 0 1 1-1ZM5 18a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

// --- External playback controls -----------------------------------------------------------
// Drives a ready video.js player from buttons rendered outside the player chrome (play/pause,
// single-frame stepping, ±5s skip, slow-motion speeds). Replaces the old in-player custom
// buttons whose Bootstrap-Icon glyphs never loaded — they were invisible but still clickable.

// Minimal shape of the video.js player we use here (video.js itself ships no types in this app).
interface VjsPlayer {
  play(): Promise<void> | void;
  pause(): void;
  paused(): boolean;
  currentTime(): number;
  currentTime(seconds: number): void;
  playbackRate(): number;
  playbackRate(rate: number): void;
  duration(): number;
  error(): { code?: number; message?: string } | null;
  src(sources: { src: string; type: string }[]): void;
  on(event: string, handler: () => void): void;
  off(event: string, handler: () => void): void;
}

const FRAME_STEP_SECONDS = 1 / 30; // ~one frame at 30fps
const SKIP_SECONDS = 5;
const PLAYBACK_RATES = [0.25, 0.5, 1, 2];
// A play attempt that buffers this long with no forward progress is treated as unplayable.
// The long game recordings sometimes come back with segment URLs the backend can't serve, so
// every .ts 404s and video.js buffers forever without firing a prompt error.
const STALL_TIMEOUT_MS = 12000;

const ctrlIcon = (path: string) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d={path} />
  </svg>
);
const Rewind5Icon = () => ctrlIcon("M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z");
const Forward5Icon = () => ctrlIcon("M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z");
const PrevFrameIcon = () => ctrlIcon("M6 6h2v12H6zm3.5 6l8.5 6V6z");
const NextFrameIcon = () => ctrlIcon("M6 18l8.5-6L6 6v12zM16 6v12h2V6z");
const PlayIcon = () => ctrlIcon("M8 5v14l11-7z");
const PauseIcon = () => ctrlIcon("M6 5h4v14H6zm8 0h4v14h-4z");

function VideoControls({ player }: { player: VjsPlayer }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [rate, setRate] = useState(1);

  useEffect(() => {
    const syncPlay = () => setIsPlaying(!player.paused());
    const syncRate = () => setRate(player.playbackRate());
    player.on("play", syncPlay);
    player.on("pause", syncPlay);
    player.on("ratechange", syncRate);
    syncPlay();
    syncRate();
    return () => {
      // The player may already be disposed (sibling VideoJS cleanup / StrictMode remount),
      // in which case off() throws — detaching listeners is moot once it's gone.
      try {
        player.off("play", syncPlay);
        player.off("pause", syncPlay);
        player.off("ratechange", syncRate);
      } catch {
        /* player already disposed */
      }
    };
  }, [player]);

  const seekBy = (seconds: number, pauseFirst: boolean) => {
    if (pauseFirst) player.pause();
    const duration = player.duration() || Number.POSITIVE_INFINITY;
    player.currentTime(Math.min(Math.max(0, player.currentTime() + seconds), duration));
  };

  const togglePlay = () => {
    if (player.paused()) {
      const result = player.play();
      if (result && typeof (result as Promise<void>).then === "function") {
        (result as Promise<void>).catch(() => {});
      }
    } else {
      player.pause();
    }
  };

  return (
    <div className={Styles.controls}>
      <div className={Styles.controlGroup}>
        <button type="button" className={Styles.controlButton} onClick={() => seekBy(-SKIP_SECONDS, false)} title="Back 5 seconds" aria-label="Back 5 seconds">
          <Rewind5Icon />
        </button>
        <button type="button" className={Styles.controlButton} onClick={() => seekBy(-FRAME_STEP_SECONDS, true)} title="Previous frame" aria-label="Previous frame">
          <PrevFrameIcon />
        </button>
        <button type="button" className={`${Styles.controlButton} ${Styles.controlButtonPrimary}`} onClick={togglePlay} title={isPlaying ? "Pause" : "Play"} aria-label={isPlaying ? "Pause" : "Play"}>
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button type="button" className={Styles.controlButton} onClick={() => seekBy(FRAME_STEP_SECONDS, true)} title="Next frame" aria-label="Next frame">
          <NextFrameIcon />
        </button>
        <button type="button" className={Styles.controlButton} onClick={() => seekBy(SKIP_SECONDS, false)} title="Forward 5 seconds" aria-label="Forward 5 seconds">
          <Forward5Icon />
        </button>
      </div>

      <div className={Styles.speedGroup}>
        <span className={Styles.speedLabel}>Speed</span>
        {PLAYBACK_RATES.map((value) => (
          <button
            key={value}
            type="button"
            className={value === rate ? `${Styles.speedChip} ${Styles.speedChipActive}` : Styles.speedChip}
            onClick={() => player.playbackRate(value)}
            aria-pressed={value === rate}
          >
            {value}&times;
          </button>
        ))}
      </div>
    </div>
  );
}

// "1h 12m 30s" / "2:05" style label from a duration in seconds.
function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return "";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Slugify a base name into something safe for a download filename.
function buildFilename(teamName: string | undefined, video: Team.VideoAsset, extension: string): string {
  const datePart = video.created_at ? new Date(video.created_at).toISOString().slice(0, 10) : "video";
  const namePart = (teamName ?? "gamechanger")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${namePart || "gamechanger"}-${datePart}.${extension}`;
}

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" width="34" height="34" fill="currentColor" aria-hidden="true">
    <path d="M12 2 1 21h22L12 2zm0 6a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1zm0 10.5a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
  </svg>
);

// Maps a video.js MediaError to a viewer-facing reason. Code 4 (SRC_NOT_SUPPORTED) is a format
// problem; everything else (network/decode, including a 403/404 behind every segment) reads as
// "couldn't load" — which also covers the access-restricted case.
function playbackErrorMessage(error: { code?: number; message?: string } | null | undefined): string {
  if (error?.code === 4) return "This video's format isn't supported by your browser.";
  return "This video couldn't be loaded. It may be unavailable, still processing, or restricted.";
}

function VideoCard({ teamName, playlistUrl, video }: { teamName?: string; playlistUrl: string; video: Team.VideoAsset }) {
  const { status, percent, error, start, cancel } = useVideoDownload();
  const [player, setPlayer] = useState<VjsPlayer | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  const options = useMemo(
    () => ({
      controls: true,
      autoplay: false,
      preload: "none" as const,
      poster: video.thumbnail_url,
      // Shown on the TV while casting (Cast receiver metadata).
      castTitle: [teamName, video.created_at ? formatDate(video.created_at) : null].filter(Boolean).join(" — "),
      // `fill` makes the player fill the fixed 16:9 .playerWrapper rather than booting at
      // video.js's tiny intrinsic size and snapping to full size once the manifest loads.
      fill: true,
      sources: [{ src: playlistUrl, type: "application/x-mpegURL" }],
    }),
    [playlistUrl, video.thumbnail_url, video.created_at, teamName]
  );

  const handleDownload = () => {
    const filename = buildFilename(teamName, video, hlsDownloadExtension(playlistUrl));
    start(playlistUrl, filename);
  };

  // Check up front that the clip is reachable so a broken video shows its reason immediately,
  // rather than only after a play attempt trips the stall watchdog below.
  useEffect(() => {
    const controller = new AbortController();
    preflightHlsPlayable(playlistUrl, controller.signal).then((result) => {
      if (!controller.signal.aborted && !result.ok) {
        setPlaybackError(result.reason ?? playbackErrorMessage(null));
      }
    });
    return () => controller.abort();
  }, [playlistUrl]);

  // Watch the ready player so a clip that can't play says so on the player instead of spinning
  // silently. We surface video.js's own `error`, and — because dead segments stall rather than
  // error promptly — also time out a play attempt that buffers without ever making progress.
  useEffect(() => {
    if (!player) return;

    let stallTimer: number | undefined;
    const clearStall = () => {
      if (stallTimer !== undefined) window.clearTimeout(stallTimer);
      stallTimer = undefined;
    };
    const fail = (message: string) => {
      clearStall();
      try {
        player.pause(); // stop the futile buffering/spinner once we've given up
      } catch {
        /* player already disposed */
      }
      setPlaybackError(message);
    };
    const armStall = () => {
      clearStall();
      stallTimer = window.setTimeout(() => fail(playbackErrorMessage(null)), STALL_TIMEOUT_MS);
    };
    // Any forward progress means the clip is healthy: cancel the watchdog and clear a stale error.
    const onProgress = () => {
      clearStall();
      setPlaybackError(null);
    };
    const onError = () => fail(playbackErrorMessage(player.error()));

    player.on("play", armStall);
    player.on("waiting", armStall);
    player.on("playing", clearStall);
    player.on("timeupdate", onProgress);
    player.on("pause", clearStall);
    player.on("ended", clearStall);
    player.on("error", onError);

    return () => {
      clearStall();
      // off() throws if the player was already disposed (sibling cleanup / StrictMode remount).
      try {
        player.off("play", armStall);
        player.off("waiting", armStall);
        player.off("playing", clearStall);
        player.off("timeupdate", onProgress);
        player.off("pause", clearStall);
        player.off("ended", clearStall);
        player.off("error", onError);
      } catch {
        /* player already disposed */
      }
    };
  }, [player]);

  const handleRetry = () => {
    setPlaybackError(null);
    if (!player) return;
    try {
      // Re-set the source so a previously errored player fully re-attempts rather than resuming.
      player.src(options.sources);
      const result = player.play();
      if (result && typeof (result as Promise<void>).then === "function") {
        (result as Promise<void>).catch(() => {});
      }
    } catch {
      /* player already disposed */
    }
  };

  return (
    <div className={Styles.card}>
      <div className={Styles.playerWrapper}>
        <VideoJS options={options} onReady={(readyPlayer: VjsPlayer) => setPlayer(readyPlayer)} />
        {playbackError && (
          <div className={Styles.playerError} role="alert">
            <WarningIcon />
            <p className={Styles.playerErrorText}>{playbackError}</p>
            <button type="button" className={Styles.playerErrorRetry} onClick={handleRetry}>
              Try again
            </button>
          </div>
        )}
      </div>

      {player && <VideoControls player={player} />}

      <div className={Styles.cardFooter}>
        <div className={Styles.meta}>
          {video.created_at && <span className={Styles.metaDate}>{formatDate(video.created_at)}</span>}
          {video.duration > 0 && (
            <>
              <span className={Styles.metaDot} aria-hidden="true">
                •
              </span>
              <span className={Styles.metaDuration}>{formatDuration(video.duration)}</span>
            </>
          )}
        </div>

        {status === "downloading" ? (
          <div className={Styles.progressGroup}>
            <div className={Styles.progressBar}>
              <div className={Styles.progressFill} style={{ width: `${percent}%` }} />
            </div>
            <span className={Styles.progressLabel}>{percent}%</span>
            <button type="button" className={Styles.cancelButton} onClick={cancel} aria-label="Cancel download">
              ×
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={status === "error" ? `${Styles.downloadButton} ${Styles.downloadButtonError}` : Styles.downloadButton}
            onClick={handleDownload}
            title={status === "error" ? error ?? "Download failed" : "Download this clip"}
          >
            {status === "done" ? (
              <>
                <CheckIcon /> Downloaded
              </>
            ) : status === "error" ? (
              <>
                <DownloadIcon /> Retry
              </>
            ) : (
              <>
                <DownloadIcon /> Download
              </>
            )}
          </button>
        )}
      </div>

      {status === "error" && error && <div className={Styles.errorText}>{error}</div>}
    </div>
  );
}

export default function EventVideoPage() {
  const { id, eid } = useParams();
  const { data: team, isLoading: teamLoading } = useGetTeamByIDQuery(id ?? "");
  const { data: videos, isLoading: videoLoading } = useGetTeamVideoAssetsByIDQuery(id ?? "");

  if (teamLoading || videoLoading) return <PageLoader1 />;

  const eventVideos = (videos ?? []).filter((video) => video.schedule_event_id === eid);

  if (eventVideos.length === 0) {
    return (
      <div className={Styles.container}>
        <div className={Styles.emptyState}>No videos for this game yet.</div>
      </div>
    );
  }

  return (
    <div className={Styles.container}>
      {eventVideos.length > 1 && (
        <div className={Styles.header}>
          {eventVideos.length} videos
        </div>
      )}
      <div className={Styles.grid}>
        {eventVideos.map((video) => (
          <VideoCard
            key={video.id}
            teamName={team?.name}
            video={video}
            playlistUrl={`${process.env.REACT_APP_API_SERVER_URL}/teams/${id}/schedule/${eid}/videos/${video.id}/playlist.m3u8`}
          />
        ))}
      </div>
    </div>
  );
}
